/**
 * ─────────────────────────────────────────────
 * src/services/projectService.js
 * ─────────────────────────────────────────────
 *
 * Covers every project-related API call used across
 * Layout.js, Projects.js, and Kanban.js.
 *
 * Routes covered:
 *   POST   /api/projects                              → create project
 *   GET    /api/projects/:id                          → get single project
 *   GET    /api/projects/:id/lists                    → get board (lists + tasks)
 *   GET    /api/projects/:id/summary                  → trackingStatus, timeline, members
 *   POST   /api/projects/:id/members                  → add member
 *   DELETE /api/projects/:id/members/:userId          → remove member
 *   GET    /api/admin/users                           → all users (member picker)
 *   GET    /api/projects/:id/activity/export          → CSV download
 */

import api from "../lib/api";

// ─────────────────────────────────────────────
// CREATE PROJECT
// Payload: { name, description, startDate, deadline, priority, members, list, task }
// Returns: { success, data: { project, list, task } }
// ─────────────────────────────────────────────
const createProject = async (projectData) => {
  const response = await api.post("/projects", projectData);
  return response.data;
};

// ─────────────────────────────────────────────
// CLIENT ACCESS ON A PROJECT
// getAvailableClients → viewer accounts not already on the project
// inviteClient        → invites a NEW client, or attaches an existing viewer
//                       (the backend handles both cases on the same route)
// ─────────────────────────────────────────────
const getAvailableClients = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/available-clients`);
  return response.data;
};

const inviteClient = async (projectId, payload) => {
  const response = await api.post(`/projects/${projectId}/invite-client`, payload);
  return response.data;
};

// ─────────────────────────────────────────────
// GET SINGLE PROJECT
// Returns: { success, data: project }
// ─────────────────────────────────────────────
const getProjectById = async (projectId) => {
  const response = await api.get(`/projects/${projectId}`);
  return response.data;
};

// ─────────────────────────────────────────────
// GET BOARD (lists + tasks for kanban)
// Returns: { success, data: [ { _id, name, order, tasks: [...] }, ... ] }
// ─────────────────────────────────────────────
const getProjectLists = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/lists`);
  return response.data;
};

// ─────────────────────────────────────────────
// GET PROJECT SUMMARY (for Projects.js detail view)
// Returns: { success, data: { trackingStatus, daysLeft, members, timeline, ... } }
// ─────────────────────────────────────────────
const getProjectSummary = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/summary`);
  return response.data;
};

// ─────────────────────────────────────────────
// ADD MEMBER
// Payload: { userId }
// Returns: { success, data: project }
// ─────────────────────────────────────────────
const addMember = async (projectId, userId) => {
  const response = await api.post(`/projects/${projectId}/members`, { userId });
  return response.data;
};

// ─────────────────────────────────────────────
// REMOVE MEMBER
// Returns: { success, data: project }
// ─────────────────────────────────────────────
const removeMember = async (projectId, userId) => {
  const response = await api.delete(`/projects/${projectId}/members/${userId}`);
  return response.data;
};

// ─────────────────────────────────────────────
// GET ALL USERS (for member picker / assignee picker)
// Returns: { success, count, data: [...users] }
// ─────────────────────────────────────────────
const getAllUsers = async (picker = false) => {
  const url = picker ? "/admin/users?picker=true" : "/admin/users";
  const response = await api.get(url);
  return response.data;
};

// ─────────────────────────────────────────────
// EXPORT ACTIVITY CSV
// Returns a Blob (binary CSV file)
// ─────────────────────────────────────────────
const exportActivityCSV = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/activity/export`, {
    responseType: "blob", // axios needs this for binary responses
  });
  return response; // full response so caller can get headers too
};

// One Excel workbook for several projects (a sheet per project) — only the
// ids given.
const exportProjectsActivityCSV = async (projectIds) => {
  const response = await api.get("/projects/activity/export", {
    params: { ids: projectIds.join(",") },
    responseType: "blob",
  });
  return response;
};

const updateProject = async (projectId, updates) => {
  try {
    const response = await api.patch(`/projects/${projectId}`, updates);
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 405) {
      const response = await api.put(`/projects/${projectId}`, updates);
      return response.data;
    }
    throw error;
  }
};
const disableProject = async (projectId) => {
  const response = await api.patch(`/projects/${projectId}/disable`);
  return response.data;
};

const enableProject = async (projectId) => {
  const response = await api.patch(`/projects/${projectId}/enable`);
  return response.data;
};

// ─────────────────────────────────────────────
// GET PROJECT ACTIVITY LOGS (paginated, 20 per page — server-enforced)
// Returns: { success, project, data: [...logs], pagination }
// ─────────────────────────────────────────────
const getProjectLogs = async (projectId, page = 1) => {
  const response = await api.get(`/projects/${projectId}/logs`, {
    params: { page },
  });
  return response.data;
};

// ─────────────────────────────────────────────
// DELETE ALL PROJECT ACTIVITY LOGS (admin only, enforced server-side)
// Returns: { success, message }
// ─────────────────────────────────────────────
const deleteProjectLogs = async (projectId) => {
  const response = await api.delete(`/projects/${projectId}/logs`);
  return response.data;
};

const projectService = {
  createProject,
  getAvailableClients,
  inviteClient,
  getProjectById,
  getProjectLists,
  getProjectSummary,
  addMember,
  removeMember,
  getAllUsers,
  exportActivityCSV,
  exportProjectsActivityCSV,
  updateProject,
  disableProject,
  enableProject,
  getProjectLogs,
  deleteProjectLogs,
};

export default projectService;
